<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const selectedTabIndex = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(15);
const isSyncingTemplates = ref(false);
const selectedInboxFilter = ref('all');

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const tabs = computed(() => [
  {
    key: 'template-library',
    name: t('CAMPAIGN.TEMPLATES.TABS.LIBRARY'),
  },
  {
    key: 'template-management',
    name: t('CAMPAIGN.TEMPLATES.TABS.MANAGEMENT'),
  },
]);

const selectedTabKey = computed(() => tabs.value[selectedTabIndex.value]?.key);

const pageSizeOptions = [
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

const inboxFilterOptions = computed(() => [
  {
    value: 'all',
    label: t('CAMPAIGN.TEMPLATES.MANAGEMENT.FILTER_ALL_INBOXES'),
  },
  ...(whatsappInboxes.value || []).map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);

const tableHeaders = computed(() => [
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.ID'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.NAME'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.CATEGORY'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.TYPE'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.LANGUAGE'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.STATUS'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.INBOX'),
  t('CAMPAIGN.TEMPLATES.TABLE.HEADERS.UPDATED_AT'),
]);

const formatDateTime = value => {
  if (!value) return t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE');

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const templateType = template => {
  const components = Array.isArray(template.components)
    ? template.components
    : [];
  const headerComponent = components.find(
    component => component.type === 'HEADER'
  );
  if (headerComponent?.format) return headerComponent.format.toLowerCase();
  if (components.some(component => component.type === 'BUTTONS')) {
    return 'buttons';
  }
  return 'text';
};

const normalizeTemplate = (template, inbox) => {
  const templateId =
    template.id || `${inbox.id}-${template.name}-${template.language}`;
  const templateUpdatedAt =
    template.updated_time ||
    template.modified_time ||
    template.created_at ||
    inbox.message_templates_last_updated;
  const sortTimestamp = templateUpdatedAt
    ? new Date(templateUpdatedAt).getTime()
    : 0;

  return {
    id: templateId,
    name: template.name || t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE'),
    category: template.category || t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE'),
    type: templateType(template),
    language: template.language || t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE'),
    status: template.status || t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE'),
    inboxName: inbox.name,
    inboxId: inbox.id,
    inboxPhone:
      inbox.phone_number ||
      inbox.channel?.phone_number ||
      t('CAMPAIGN.TEMPLATES.TABLE.EMPTY_VALUE'),
    updatedAt: templateUpdatedAt,
    sortTimestamp,
  };
};

const templates = computed(() => {
  const rows = (whatsappInboxes.value || []).flatMap(inbox => {
    const sourceTemplates =
      inbox.message_templates || inbox.additional_attributes?.message_templates;

    if (!Array.isArray(sourceTemplates)) return [];

    return sourceTemplates.map(template => normalizeTemplate(template, inbox));
  });

  return rows.sort((a, b) => {
    if (a.sortTimestamp !== b.sortTimestamp) {
      return b.sortTimestamp - a.sortTimestamp;
    }

    if (a.inboxId !== b.inboxId) {
      return a.inboxId - b.inboxId;
    }

    const nameComparison = String(a.name).localeCompare(String(b.name));
    if (nameComparison !== 0) {
      return nameComparison;
    }

    return String(a.id).localeCompare(String(b.id));
  });
});

const filteredTemplates = computed(() => {
  if (selectedInboxFilter.value === 'all') {
    return templates.value;
  }

  const selectedInboxId = Number(selectedInboxFilter.value);
  return templates.value.filter(
    template => Number(template.inboxId) === selectedInboxId
  );
});

const totalTemplates = computed(() => filteredTemplates.value.length);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalTemplates.value / itemsPerPage.value))
);
const paginatedTemplates = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredTemplates.value.slice(start, start + itemsPerPage.value);
});

const pageStart = computed(() => {
  if (!totalTemplates.value) return 0;
  return (currentPage.value - 1) * itemsPerPage.value + 1;
});
const pageEnd = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalTemplates.value)
);

const statusClass = status => {
  const normalizedStatus = String(status).toLowerCase();
  return {
    'bg-n-teal-3 text-n-teal-11': normalizedStatus === 'approved',
    'bg-n-ruby-3 text-n-ruby-11': normalizedStatus === 'rejected',
    'bg-n-amber-3 text-n-amber-11': normalizedStatus === 'pending',
    'bg-n-slate-3 text-n-slate-11': ![
      'approved',
      'rejected',
      'pending',
    ].includes(normalizedStatus),
  };
};

const statusLabel = status => {
  const normalizedStatus = String(status).toLowerCase();

  if (normalizedStatus === 'approved') {
    return t('CAMPAIGN.TEMPLATES.TABLE.STATUS.APPROVED');
  }
  if (normalizedStatus === 'rejected') {
    return t('CAMPAIGN.TEMPLATES.TABLE.STATUS.REJECTED');
  }
  if (normalizedStatus === 'pending') {
    return t('CAMPAIGN.TEMPLATES.TABLE.STATUS.PENDING');
  }

  return normalizedStatus;
};

const onTabChange = index => {
  selectedTabIndex.value = index;
};

const goToPage = page => {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
};

const handleCreateTemplate = () => {
  router.push({ name: 'campaigns_templates_new' });
};

const syncTemplates = async () => {
  if (!whatsappInboxes.value?.length) return;

  isSyncingTemplates.value = true;
  try {
    await Promise.all(
      whatsappInboxes.value.map(inbox =>
        store.dispatch('inboxes/syncTemplates', inbox.id)
      )
    );
    await store.dispatch('inboxes/get');
    useAlert(t('CAMPAIGN.TEMPLATES.MANAGEMENT.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.TEMPLATES.MANAGEMENT.SYNC_ERROR'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
});

watch(itemsPerPage, () => {
  currentPage.value = 1;
});

watch(selectedInboxFilter, () => {
  currentPage.value = 1;
});

watch(totalPages, value => {
  if (currentPage.value > value) {
    currentPage.value = value;
  }
});
</script>

<template>
  <div class="grid grid-rows-[auto_1fr] h-full w-full min-w-0 bg-n-surface-1">
    <SettingIntroBanner
      :header-title="t('CAMPAIGN.TEMPLATES.HEADER_TITLE')"
      :header-content="t('CAMPAIGN.TEMPLATES.HEADER_SUBTITLE')"
    >
      <woot-tabs
        class="[&_ul]:p-0 top-px relative"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
          is-compact
        />
      </woot-tabs>
    </SettingIntroBanner>

    <section class="w-full overflow-auto py-8">
      <div class="max-w-7xl mx-auto w-full px-6">
        <div
          v-if="selectedTabKey === 'template-library'"
          class="max-w-4xl rounded-lg border border-n-weak bg-n-alpha-2 p-6"
        >
          <h3 class="mb-2 text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.TEMPLATES.LIBRARY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.TEMPLATES.LIBRARY.DESCRIPTION') }}
          </p>
        </div>

        <div v-if="selectedTabKey === 'template-management'">
          <div class="mb-6 flex flex-col justify-between gap-4 sm:flex-row">
            <div class="min-w-0">
              <h3 class="mb-1 text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.MANAGEMENT.TITLE') }}
              </h3>
              <p class="max-w-3xl text-sm text-n-slate-11">
                {{ t('CAMPAIGN.TEMPLATES.MANAGEMENT.DESCRIPTION') }}
              </p>
            </div>
            <div class="flex shrink-0 flex-wrap items-center gap-3">
              <ComboBox
                v-model="selectedInboxFilter"
                :options="inboxFilterOptions"
                class="min-w-48 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              />
              <Button
                icon="i-lucide-plus"
                :label="t('CAMPAIGN.TEMPLATES.MANAGEMENT.CREATE_BUTTON')"
                size="sm"
                @click="handleCreateTemplate"
              />
              <Button
                icon="i-lucide-refresh-cw"
                :label="t('CAMPAIGN.TEMPLATES.MANAGEMENT.SYNC_BUTTON')"
                variant="faded"
                color="teal"
                size="sm"
                :is-loading="isSyncingTemplates"
                :disabled="isSyncingTemplates || !whatsappInboxes?.length"
                @click="syncTemplates"
              />
            </div>
          </div>

          <div
            class="overflow-visible rounded-lg border border-n-weak bg-n-surface-1 ltr:[&_td:first-child]:pl-4 ltr:[&_th:first-child]:pl-4 rtl:[&_td:first-child]:pr-4 rtl:[&_th:first-child]:pr-4"
          >
            <div class="overflow-x-auto overflow-y-visible">
              <BaseTable
                :headers="tableHeaders"
                :items="paginatedTemplates"
                :no-data-message="t('CAMPAIGN.TEMPLATES.TABLE.EMPTY')"
              >
                <template #row="{ items }">
                  <BaseTableRow
                    v-for="(template, index) in items"
                    :key="`${template.id}-${template.inboxName}`"
                    :item="template"
                  >
                    <template #default>
                      <BaseTableCell class="w-16">
                        {{ pageStart + index }}
                      </BaseTableCell>
                      <BaseTableCell class="min-w-56 max-w-0">
                        <div class="truncate text-n-slate-12">
                          {{ template.name }}
                        </div>
                        <div class="truncate text-xs text-n-slate-10">
                          {{ template.id }}
                        </div>
                      </BaseTableCell>
                      <BaseTableCell class="w-36">
                        {{ template.category }}
                      </BaseTableCell>
                      <BaseTableCell class="w-28">
                        {{ template.type }}
                      </BaseTableCell>
                      <BaseTableCell class="w-28">
                        {{ template.language }}
                      </BaseTableCell>
                      <BaseTableCell class="w-36">
                        <span
                          class="inline-flex rounded-md px-2 py-1 text-xs font-medium capitalize"
                          :class="statusClass(template.status)"
                        >
                          {{ statusLabel(template.status) }}
                        </span>
                      </BaseTableCell>
                      <BaseTableCell class="min-w-48 max-w-0">
                        <span class="truncate block">
                          {{ template.inboxName }}
                        </span>
                        <span class="truncate block text-xs text-n-slate-10">
                          #{{ template.inboxId }} · {{ template.inboxPhone }}
                        </span>
                      </BaseTableCell>
                      <BaseTableCell class="w-44">
                        {{ formatDateTime(template.updatedAt) }}
                      </BaseTableCell>
                    </template>
                  </BaseTableRow>
                </template>
              </BaseTable>
            </div>

            <div
              v-if="totalTemplates"
              class="flex min-h-[3.375rem] flex-col gap-4 border-t border-n-weak px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div
                class="flex flex-wrap items-center gap-3 text-sm text-n-slate-11"
              >
                <span>
                  {{ t('CAMPAIGN.TEMPLATES.TABLE.ROWS_SHORT') }}
                </span>
                <ComboBox
                  v-model="itemsPerPage"
                  :options="pageSizeOptions"
                  class="w-20 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
                />
                <span>
                  {{
                    t('CAMPAIGN.TEMPLATES.TABLE.PAGE_RANGE', {
                      start: pageStart,
                      end: pageEnd,
                      total: totalTemplates,
                    })
                  }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  icon="i-lucide-chevron-left"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  class="!h-8 !w-8"
                  :disabled="currentPage === 1"
                  @click="goToPage(currentPage - 1)"
                />
                <span class="text-sm text-n-slate-11">
                  {{
                    t('CAMPAIGN.TEMPLATES.TABLE.PAGE_COUNT', {
                      current: currentPage,
                      total: totalPages,
                    })
                  }}
                </span>
                <Button
                  icon="i-lucide-chevron-right"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  class="!h-8 !w-8"
                  :disabled="currentPage === totalPages"
                  @click="goToPage(currentPage + 1)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
